import React from 'react';

const Invoice = ({ bookingDetails }) => {
  const {
    bookingName,
    guestName,
    bookingId,
    checkInDate,
    checkOutDate,
    beds,
    nights,
    bedNumbers,
    ratePerNight,
    taxRate,
    extraCharges = [],
    checkInTime,
    checkOutTime,
    packageInfo,
    totalAmount,
    applyConvenienceFee,
    convenienceFeeRate,
    amountPaid
  } = bookingDetails;

  // For hourly plans, checkout date should be same as checkin date
  const displayCheckOutDate = packageInfo?.plan === 'Hourly Plan' ? checkInDate : checkOutDate;

  // Use package pricing if available, otherwise fall back to rate calculation
  const actualRate = packageInfo?.price || ratePerNight || 600;
  
  // Calculate bed subtotal based on package type
  let bedSubtotal;
  if (packageInfo?.price) {
    // Package pricing
    if (packageInfo.plan === 'Hourly Plan') {
      bedSubtotal = packageInfo.price * beds; // No nights multiplication for hourly
    } else if (packageInfo.plan === 'Weekly Plan' || packageInfo.plan === 'Monthly Plan') {
      bedSubtotal = packageInfo.price * beds; // Weekly/Monthly already include duration
    } else {
      bedSubtotal = packageInfo.price * beds * nights; // Regular plan per night
    }
  } else {
    // Regular dormitory pricing
    bedSubtotal = actualRate * beds * nights;
  }
  
  const extraTotal = extraCharges.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);
  const subtotal = bedSubtotal + extraTotal;
  const feeRate = typeof convenienceFeeRate === 'number' ? convenienceFeeRate : 0.05;
  const includeFee = (applyConvenienceFee === undefined) ? true : !!applyConvenienceFee;
  const convenienceFee = includeFee ? Math.round(subtotal * feeRate) : 0; // optional convenience fee
  // Use the totalAmount from booking if available, otherwise calculate
  const total = totalAmount || (subtotal + convenienceFee);
  const paid = parseFloat(amountPaid || 0) || 0;
  const balance = Math.max(0, total - paid);

  return (
    <div style={{
      background: '#fff',
      maxWidth: 800,
      minWidth: 340,
      margin: '0 auto',
      padding: 32,
      fontFamily: 'serif, Arial, sans-serif',
      color: '#222',
      border: '1.2px solid #222',
      borderRadius: 8,
      boxSizing: 'border-box',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      minHeight: 900
    }}>
      {/* Header Row */}
      <div style={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', borderBottom: '2px solid #222', paddingBottom: 16, marginBottom: 28}}>
        <div style={{flex: '0 0 auto', marginRight: 18}}>
          <img src="/logo192.png" alt="Logo" style={{height: 34, width: 34, objectFit: 'contain'}} />
        </div>
        <div style={{flex: 1, minWidth: 0, textAlign: 'left'}}>
          <div style={{fontWeight: 700, fontSize: 22, letterSpacing: 1, marginBottom: 2}}>Skyline Backpackers</div>
          <div style={{fontSize: 13, color: '#444', lineHeight: 1.5}}>
            3rd Floor, ICICI Bank, 1-8, 303/30, Shree Arcade Building, PG Road, Sindhi Colony, Begumpet, Hyderabad, Telangana 500003<br />
            +91 90327 97797<br />
            booking@skylinebackpackers.in
          </div>
        </div>
        <div style={{flex: '0 0 180px', textAlign: 'right', fontSize: 14, color: '#222'}}>
          <div style={{fontWeight: 700, fontSize: 18, letterSpacing: 2, marginBottom: 2}}>INVOICE</div>
          <div style={{fontWeight: 500, fontSize: 13, marginBottom: 6}}>Booking ID: {bookingId || 'SKY00000'}</div>
          <div style={{fontWeight: 500, fontSize: 13, marginBottom: 6}}>Guests: {bookingDetails.guests || beds || 1}</div>
          <div style={{fontSize: 13}}>DATE</div>
          <div style={{fontSize: 13, marginBottom: 8}}>{new Date().toLocaleDateString()}</div>
          <div style={{fontSize: 13}}>Check-in</div>
          <div style={{fontWeight: 500, fontSize: 13, marginBottom: 2}}>{checkInDate || 'N/A'}</div>
          <div style={{fontSize: 13}}>Check-out</div>
          <div style={{fontWeight: 500, fontSize: 13}}>{displayCheckOutDate || 'N/A'}</div>
        </div>
      </div>

      {/* Bill To */}
      <div style={{fontWeight: 700, fontSize: 14, letterSpacing: 1, marginTop: 20, marginBottom: 2}}>BILL TO</div>
      <div style={{fontWeight: 600, fontSize: 16, marginBottom: 2}}>{(bookingDetails.guestDetails && bookingDetails.guestDetails.name) ? bookingDetails.guestDetails.name : (guestName || bookingName || 'Guest')}</div>
      {bookingDetails.guestDetails && (
        <div style={{fontSize: 14, marginBottom: 2, color: '#444'}}>
          {bookingDetails.guestDetails.email && (<div>Email: {bookingDetails.guestDetails.email}</div>)}
          {bookingDetails.guestDetails.phone && (<div>Phone: {bookingDetails.guestDetails.phone}</div>)}
        </div>
      )}
      
      {/* Package Information */}
      {packageInfo && (
        <div style={{marginTop: 16, marginBottom: 16, padding: 12, background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 4}}>
          <div style={{fontWeight: 700, fontSize: 14, letterSpacing: 1, marginBottom: 4}}>PACKAGE SELECTED</div>
          <div style={{fontWeight: 600, fontSize: 15, marginBottom: 2}}>{packageInfo.plan}</div>
          <div style={{fontSize: 14, color: '#666'}}>{packageInfo.duration} - ₹{packageInfo.price}</div>
        </div>
      )}
      
      <div style={{marginBottom: 16}}></div>
      
      {/* Check-in/Check-out Times */}
      {(checkInTime || checkOutTime) && (
        <div style={{marginBottom: 16, fontSize: 14}}>
          {checkInTime && <div>Check-in Time: {new Date(checkInTime).toLocaleString()}</div>}
          {checkOutTime && <div>Check-out Time: {new Date(checkOutTime).toLocaleString()}</div>}
        </div>
      )}

      {/* Table */}
      <table style={{width: '100%', borderCollapse: 'collapse', marginBottom: 18, fontSize: 14}}>
        <thead>
          <tr style={{background: '#f8f8f8'}}>
            <th style={{border: '1px solid #222', padding: '7px 8px', fontWeight: 700, fontSize: 13}}>Service</th>
            <th style={{border: '1px solid #222', padding: '7px 8px', fontWeight: 700, fontSize: 13}}>Rate</th>
            <th style={{border: '1px solid #222', padding: '7px 8px', fontWeight: 700, fontSize: 13}}>Quantity</th>
            <th style={{border: '1px solid #222', padding: '7px 8px', fontWeight: 700, fontSize: 13}}>Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{border: '1px solid #222', padding: '7px 8px'}}>
              {packageInfo ? `${packageInfo.plan} - ${packageInfo.duration}` : 'Dormitory Bed'}
            </td>
            <td style={{border: '1px solid #222', padding: '7px 8px'}}>
              {packageInfo ? `₹${packageInfo.price.toFixed(2)}` : `₹${actualRate.toFixed(2)}`}
            </td>
            <td style={{border: '1px solid #222', padding: '7px 8px'}}>
              {packageInfo ? (packageInfo.plan === 'Hourly Plan' ? `${beds} bed${beds > 1 ? 's' : ''}` : `${beds} bed${beds > 1 ? 's' : ''} × ${nights} night${nights > 1 ? 's' : ''}`) : `${beds} × ${nights} = ${beds * nights}`}
            </td>
            <td style={{border: '1px solid #222', padding: '7px 8px'}}>₹{bedSubtotal.toFixed(2)}</td>
          </tr>
          {extraCharges && extraCharges.length > 0 && extraCharges.map((charge, idx) => (
            <tr key={idx}>
              <td style={{border: '1px solid #222', padding: '7px 8px'}}>{charge.label || 'Extra Charge'}</td>
              <td style={{border: '1px solid #222', padding: '7px 8px'}}>-</td>
              <td style={{border: '1px solid #222', padding: '7px 8px'}}>-</td>
              <td style={{border: '1px solid #222', padding: '7px 8px'}}>₹{parseFloat(charge.amount||0).toFixed(2)}</td>
            </tr>
          ))}
          {(bedNumbers || bookingDetails.selectedBeds || bookingDetails.bookingData?.selectedBeds) && (
            <tr>
              <td colSpan="4" style={{ textAlign: 'left', fontStyle: 'italic', fontSize: 13, padding: '5px 8px', border: '1px solid #222', borderTop: 'none', background: '#fafafa' }}>
                Bed Numbers: {
                  (() => {
                    const beds = bedNumbers || bookingDetails.selectedBeds || bookingDetails.bookingData?.selectedBeds || [];
                    if (bookingDetails.forceAllBeds) return 'All beds';
                    if (Array.isArray(beds)) {
                      if (beds.length >= 38) return 'All beds';
                      return beds.map(b => {
                        if (typeof b === 'string') return b;
                        if (typeof b === 'object') return b.number || b.label || b.id || b.name || JSON.stringify(b);
                        return b;
                      }).join(', ');
                    }
                    return beds ? beds.toString() : '';
                  })()
                }
              </td>
            </tr>
          )}
        </tbody> 
      </table>

      {/* Summary Table */}
      <table style={{width: '60%', marginLeft: 'auto', borderCollapse: 'collapse', fontSize: 14, marginBottom: 18}}>
        <tbody>
          <tr>
            <td style={{padding: '7px 8px', border: 'none', fontWeight: 500}}>Subtotal</td>
            <td style={{padding: '7px 8px', border: 'none', textAlign: 'right', fontFamily: 'monospace'}}>₹{subtotal.toFixed(2)}</td>
          </tr>
          {includeFee && (
            <tr>
              <td style={{padding: '7px 8px', border: 'none', fontWeight: 500}}>Convenience Fee ({(feeRate*100).toFixed(0)}%)</td>
              <td style={{padding: '7px 8px', border: 'none', textAlign: 'right', fontFamily: 'monospace'}}>₹{convenienceFee.toFixed(2)}</td>
            </tr>
          )}
          <tr>
            <td style={{padding: '7px 8px', borderTop: '2px solid #222', fontWeight: 700}}>Total</td>
            <td style={{padding: '7px 8px', borderTop: '2px solid #222', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700}}>₹{total.toFixed(2)}</td>
          </tr>
          {paid > 0 && (
            <>
              <tr>
                <td style={{padding: '7px 8px', border: 'none', fontWeight: 500}}>Amount Paid</td>
                <td style={{padding: '7px 8px', border: 'none', textAlign: 'right', fontFamily: 'monospace'}}>₹{paid.toFixed(2)}</td>
              </tr>
              <tr>
                <td style={{padding: '7px 8px', borderTop: '2px dashed #888', fontWeight: 700}}>Balance Due</td>
                <td style={{padding: '7px 8px', borderTop: '2px dashed #888', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700}}>₹{balance.toFixed(2)}</td>
              </tr>
            </>
          )}
        </tbody>
      </table>

      {/* Payment Details */}
      <div style={{marginTop: 32, fontSize: 13, borderTop: '1px solid #bbb', paddingTop: 14}}>
        <div style={{fontWeight: 700, fontSize: 14, marginBottom: 6}}>Payment Details</div>
        <div style={{marginBottom: 2}}>Account Number: <span style={{ fontWeight: 600 }}>10229579924</span></div>
        <div style={{marginBottom: 2}}>IFSC: <span style={{ fontWeight: 600 }}>IDFB0080203</span></div>
        <div>Phone: <span style={{ fontWeight: 600 }}>+91 90327 97797</span></div>
      </div>
      {/* Thank You Quote */}
      <div style={{marginTop: 20, fontSize: 15, fontStyle: 'italic', color: '#176b34', textAlign: 'center', letterSpacing: 0.2}}>
        Thank you for choosing Skyline Backpackers!<br />We hope you enjoyed your stay — do visit us again soon.
      </div>
    </div>
  );
};

export default Invoice;
